let bundleItems = [];
let prebuildApplied = false;

const CREATOR_CONTEXT_STORAGE_KEY = 'cc_creator_context_v1';
const urlParams = new URLSearchParams(window.location.search);
const typeCatalogCache = new Map();

const parseHandleList = (value) => {
    if (!value) return [];

    return String(value)
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean);
};

const parseProductList = (value) => {
    if (!value) return [];

    const normalizeProduct = (product) => {
        if (!product || typeof product !== 'object') return null;

        const handle = String(product.handle || product.product_handle || '').trim().toLowerCase();
        const title = String(product.title || product.product_title || '').trim();
        const type = String(product.type || product.product_type || '').trim();
        const shopifyId = String(product.shopify_id || product.product_id || product.id || '').trim();

        if (!handle && !title && !type && !shopifyId) return null;

        return {
            handle,
            title,
            type,
            shopify_id: shopifyId
        };
    };

    if (Array.isArray(value)) {
        return value.map(normalizeProduct).filter(Boolean);
    }

    const rawString = String(value || '').trim();
    if (!rawString) return [];

    try {
        const parsed = JSON.parse(rawString);
        if (Array.isArray(parsed)) {
            return parsed.map(normalizeProduct).filter(Boolean);
        }
    } catch (error) {
        // Fall through to handle list parsing.
    }

    return parseHandleList(rawString).map((handle) => ({
        handle,
        title: '',
        type: '',
        shopify_id: ''
    }));
};

const parseRecipeValue = (value) => {
    if (!value) return [];

    if (Array.isArray(value)) {
        return value
            .map((entry) => {
                if (!entry || typeof entry !== 'object') return null;

                const handle = String(entry.handle || '').trim().toLowerCase();
                const qty = parseIntegerParam(entry.qty);
                if (!handle || !qty) return null;

                return { handle, qty };
            })
            .filter(Boolean);
    }

    return String(value)
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)
        .map((entry) => {
            const [rawHandle, rawQty] = entry.split(':');
            const handle = String(rawHandle || '').trim().toLowerCase();
            const qty = parseIntegerParam(rawQty);

            if (!handle || !qty) return null;

            return { handle, qty };
        })
        .filter(Boolean);
};

const parseIntegerParam = (value) => {
    const parsedValue = Number.parseInt(value, 10);
    if (Number.isNaN(parsedValue) || parsedValue <= 0) return null;
    return parsedValue;
};

const readCreatorContext = () => {
    try {
        return JSON.parse(window.localStorage.getItem(CREATOR_CONTEXT_STORAGE_KEY) || '{}');
    } catch (error) {
        return {};
    }
};

const requestedType = (urlParams.get('type') || '').trim().toLowerCase();
const requestedSize = parseIntegerParam(urlParams.get('size'));
const requestedCreatorHandle = (urlParams.get('cc_handle') || '').trim().toLowerCase();
const requestedDropHandles = parseHandleList(urlParams.get('cc_drop_handles'));
const requestedDropProducts = parseProductList(urlParams.get('cc_drop_products'));
const requestedPrebuildRecipe = parseRecipeValue(urlParams.get('cc_prebuild_recipe') || urlParams.get('cc_recipe'));
const hasExplicitCreatorContextInUrl = Boolean(
    requestedCreatorHandle
    || (urlParams.get('cc_discount_code') || '').trim()
    || (urlParams.get('cc_discount_pct') || '').trim()
    || (urlParams.get('cc_template_type') || '').trim()
    || requestedDropHandles.length > 0
    || requestedDropProducts.length > 0
    || requestedPrebuildRecipe.length > 0
);
const prebuildRequested = requestedDropHandles.length > 0
    || requestedDropProducts.length > 0
    || requestedPrebuildRecipe.length > 0
    || (['1', 'true', 'yes'].includes((urlParams.get('cc_prebuild') || '').toLowerCase()) && hasExplicitCreatorContextInUrl);

const readResolvedCreatorContext = () => (hasExplicitCreatorContextInUrl ? readCreatorContext() : {});

function getBundleItemsQty () {
    return bundleItems.reduce((acc, curr) => acc + curr.qty, 0);
}

function selectedBundleType () {
    const selectedTypeInput = document.querySelector('[data-section-step="1"] input[name="build-a-box-type"]:checked');
    return selectedTypeInput ? selectedTypeInput.value : 'kids';
}

function selectedBundleSize () {
    const selectedSizeInput = document.querySelector('[data-section-step="2"] input[name="build-a-box-size"]:checked');
    return selectedSizeInput ? Number(selectedSizeInput.value) : 0;
}

function getCurrentBuildButtonsByType (type) {
    return Array.from(document.querySelectorAll(`[data-section-step="3"] .product-list[data-type="${type}"] button[data-add-to-bundle]`));
}

function getAvailableTypes () {
    return Array.from(document.querySelectorAll('[data-section-step="1"] input[name="build-a-box-type"]'))
        .map((input) => input.value);
}

function normalizeHandle (value) {
    return String(value || '').trim().toLowerCase();
}

function normalizeText (value) {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function tokenizeText (value) {
    return normalizeText(value)
        .split(' ')
        .filter((token) => token.length >= 3);
}

function getTypeCatalog (type) {
    if (typeCatalogCache.has(type)) {
        return typeCatalogCache.get(type);
    }

    const catalog = getCurrentBuildButtonsByType(type)
        .filter((button) => button.dataset.soldout !== 'true')
        .map((button) => {
            const handle = normalizeHandle(button.dataset.productHandle);
            const title = String(button.dataset.productTitle || '').trim();

            return {
                button,
                handle,
                title,
                tokens: tokenizeText(`${handle} ${title}`)
            };
        });

    typeCatalogCache.set(type, catalog);
    return catalog;
}

function getResolvedDropHandles () {
    if (requestedDropHandles.length > 0) return requestedDropHandles;

    const context = readResolvedCreatorContext();
    const mostRecentDropProducts = parseProductList(context.most_recent_drop_products);
    if (mostRecentDropProducts.length > 0) {
        const productHandles = mostRecentDropProducts
            .map((product) => normalizeHandle(product.handle))
            .filter(Boolean);
        if (productHandles.length > 0) return productHandles;
    }

    const mostRecentDropHandles = parseHandleList(context.most_recent_drop_handles);
    if (mostRecentDropHandles.length > 0) return mostRecentDropHandles;

    return parseHandleList(context.featured_handles);
}

function getResolvedDropProducts () {
    if (requestedDropProducts.length > 0) return requestedDropProducts;

    const context = readResolvedCreatorContext();
    const contextDropProducts = parseProductList(context.most_recent_drop_products);
    if (contextDropProducts.length > 0) return contextDropProducts;

    const fallbackHandles = getResolvedDropHandles();
    return fallbackHandles.map((handle) => ({
        handle: normalizeHandle(handle),
        title: '',
        type: '',
        shopify_id: ''
    }));
}

function getResolvedPrebuildRecipe () {
    if (requestedPrebuildRecipe.length > 0) return requestedPrebuildRecipe;

    const context = readResolvedCreatorContext();
    return parseRecipeValue(context.prebuild_recipe || context.most_recent_drop_recipe);
}

function getDefaultPrebuildButtons (type) {
    const typeButtons = getTypeCatalog(type).map((item) => item.button);

    if (typeButtons.length === 0) return [];

    return typeButtons.slice(0, Math.min(3, typeButtons.length));
}

function inferTypeFromKeywords (product, availableTypes) {
    const keywordSource = `${product.type || ''} ${product.title || ''} ${product.handle || ''}`.toLowerCase();
    const hasCookieSignal = /cookie|cookies|soft[\s-]?baked/.test(keywordSource);
    const hasProteinSignal = /protein/.test(keywordSource);
    const hasKidsSignal = /\bkids?\b/.test(keywordSource);
    const hasBarSignal = /\bbars?\b/.test(keywordSource);

    if (availableTypes.includes('cookies') && hasCookieSignal) {
        return 'cookies';
    }

    if (availableTypes.includes('protein') && hasProteinSignal) {
        return 'protein';
    }

    if (availableTypes.includes('kids') && (hasKidsSignal || (hasBarSignal && !hasProteinSignal && !hasCookieSignal))) {
        return 'kids';
    }

    if (hasBarSignal && availableTypes.includes('protein') && !availableTypes.includes('kids')) {
        return 'protein';
    }

    if (hasBarSignal && availableTypes.includes('kids') && !availableTypes.includes('protein')) {
        return 'kids';
    }

    return null;
}

function getTokenOverlapScore (tokensA, tokensB) {
    if (tokensA.length === 0 || tokensB.length === 0) return 0;
    const tokenSetB = new Set(tokensB);
    return tokensA.reduce((score, token) => (tokenSetB.has(token) ? score + 1 : score), 0);
}

function scoreCatalogMatch (product, catalogItem) {
    const normalizedProductHandle = normalizeHandle(product.handle);
    const normalizedItemHandle = normalizeHandle(catalogItem.handle);

    if (normalizedProductHandle && normalizedProductHandle === normalizedItemHandle) {
        return 1000;
    }

    if (normalizedProductHandle && normalizedItemHandle
        && (normalizedProductHandle.includes(normalizedItemHandle) || normalizedItemHandle.includes(normalizedProductHandle))) {
        return 700;
    }

    const productTokens = tokenizeText(`${product.handle || ''} ${product.title || ''}`);
    const overlap = getTokenOverlapScore(productTokens, catalogItem.tokens);
    if (overlap > 0) {
        return overlap * 20;
    }

    return 0;
}

function findBestCatalogMatchForType (type, product) {
    const catalog = getTypeCatalog(type);
    if (catalog.length === 0) return null;

    let bestMatch = null;
    let bestScore = 0;

    catalog.forEach((catalogItem) => {
        const score = scoreCatalogMatch(product, catalogItem);
        if (score > bestScore) {
            bestScore = score;
            bestMatch = catalogItem;
        }
    });

    if (!bestMatch || bestScore <= 0) return null;

    return {
        ...bestMatch,
        score: bestScore
    };
}

function findBestCatalogType (product, availableTypes) {
    let bestType = null;
    let bestScore = 0;

    availableTypes.forEach((type) => {
        const match = findBestCatalogMatchForType(type, product);
        if (match && match.score > bestScore) {
            bestScore = match.score;
            bestType = type;
        }
    });

    if (!bestType || bestScore <= 0) return null;
    return bestType;
}

function getDetectionProducts () {
    const recipe = getResolvedPrebuildRecipe();
    if (recipe.length > 0) {
        return recipe.map((entry) => ({
            handle: normalizeHandle(entry.handle),
            title: '',
            type: '',
            weight: entry.qty
        }));
    }

    const dropProducts = getResolvedDropProducts();
    if (dropProducts.length > 0) {
        return dropProducts.map((product) => ({
            handle: normalizeHandle(product.handle),
            title: String(product.title || '').trim(),
            type: String(product.type || '').trim(),
            weight: 1
        }));
    }

    return getResolvedDropHandles().map((handle) => ({
        handle: normalizeHandle(handle),
        title: '',
        type: '',
        weight: 1
    }));
}

function determinePrebuildType () {
    const availableTypes = getAvailableTypes();
    if (availableTypes.length === 0) return selectedBundleType();

    if (requestedType && availableTypes.includes(requestedType)) {
        return requestedType;
    }

    const detectionProducts = getDetectionProducts();
    if (detectionProducts.length === 0) return selectedBundleType();

    const typeCounts = {};
    availableTypes.forEach((type) => {
        typeCounts[type] = 0;
    });

    detectionProducts.forEach((product) => {
        const weight = parseIntegerParam(product.weight) || 1;
        const normalizedHandle = normalizeHandle(product.handle);

        let matchedByExactHandle = false;
        if (normalizedHandle) {
            availableTypes.forEach((type) => {
                const hasExactHandle = getTypeCatalog(type)
                    .some((catalogItem) => catalogItem.handle === normalizedHandle);

                if (hasExactHandle) {
                    typeCounts[type] += weight;
                    matchedByExactHandle = true;
                }
            });
        }

        if (matchedByExactHandle) return;

        const keywordType = inferTypeFromKeywords(product, availableTypes);
        if (keywordType) {
            typeCounts[keywordType] += weight;
            return;
        }

        const catalogType = findBestCatalogType(product, availableTypes);
        if (catalogType) {
            typeCounts[catalogType] += weight;
        }
    });

    const cookiesCount = typeCounts.cookies || 0;
    const kidsCount = typeCounts.kids || 0;
    const proteinCount = typeCounts.protein || 0;
    const barsCount = kidsCount + proteinCount;

    if (availableTypes.includes('cookies') && cookiesCount > barsCount) {
        return 'cookies';
    }

    if (barsCount > cookiesCount) {
        if (availableTypes.includes('protein') && proteinCount > kidsCount) {
            return 'protein';
        }
        if (availableTypes.includes('kids') && kidsCount > proteinCount) {
            return 'kids';
        }

        const selectedType = selectedBundleType();
        if (selectedType === 'kids' || selectedType === 'protein') {
            return selectedType;
        }
        if (availableTypes.includes('protein')) return 'protein';
        if (availableTypes.includes('kids')) return 'kids';
    }

    let bestType = selectedBundleType();
    let bestMatchCount = -1;
    availableTypes.forEach((type) => {
        if (typeCounts[type] > bestMatchCount) {
            bestMatchCount = typeCounts[type];
            bestType = type;
        }
    });

    if (bestMatchCount > 0) return bestType;
    if (availableTypes.includes(bestType)) return bestType;
    return availableTypes[0];
}

function applyTypeSelection (type) {
    const typeInput = document.querySelector(`[data-section-step="1"] input[name="build-a-box-type"][value="${type}"]`);
    if (!typeInput) return;

    if (!typeInput.checked) {
        typeInput.checked = true;
        typeInput.dispatchEvent(new Event('change'));
    }
}

function resolveSizeForType (type) {
    const typeSizes = Array.from(document.querySelectorAll(`[data-section-step="2"] ul[data-type="${type}"] input[name="build-a-box-size"]`));

    if (typeSizes.length === 0) return null;

    if (requestedSize && typeSizes.some((sizeInput) => Number(sizeInput.value) === requestedSize)) {
        return requestedSize;
    }

    const contextDefaultSize = parseIntegerParam(readResolvedCreatorContext().default_build_box_size);
    if (contextDefaultSize && typeSizes.some((sizeInput) => Number(sizeInput.value) === contextDefaultSize)) {
        return contextDefaultSize;
    }

    const checkedSize = typeSizes.find((sizeInput) => sizeInput.checked);
    if (checkedSize) return Number(checkedSize.value);

    return Number(typeSizes[0].value);
}

function applySizeSelection (type, size) {
    if (!size) return;

    const sizeInput = document.querySelector(`[data-section-step="2"] ul[data-type="${type}"] input[name="build-a-box-size"][value="${size}"]`);
    if (!sizeInput) return;

    if (!sizeInput.checked) {
        sizeInput.checked = true;
        sizeInput.dispatchEvent(new Event('change'));
    }
}

function buildBundleItemFromButton (button) {
    return {
        title: button.dataset.productTitle,
        handle: button.dataset.productHandle,
        img: button.dataset.productImg,
        variant_id: button.dataset.variantId,
        selling_plans: button.dataset.sellingPlans.split(';'),
        qty: 1
    };
}

function isDropProductLikelyType (product, type, availableTypes) {
    const keywordType = inferTypeFromKeywords(product, availableTypes);
    if (keywordType) {
        return keywordType === type;
    }

    const catalogType = findBestCatalogType(product, availableTypes);
    if (catalogType) {
        return catalogType === type;
    }

    return true;
}

function resolveDropSourceButtons (type) {
    const dropProducts = getResolvedDropProducts();
    if (dropProducts.length === 0) return [];

    const availableTypes = getAvailableTypes();
    const matchingTypeProducts = dropProducts.filter((product) => isDropProductLikelyType(product, type, availableTypes));

    const mapProductsToButtons = (products) => products
        .map((product) => findBestCatalogMatchForType(type, product))
        .filter(Boolean)
        .map((match) => match.button);

    let sourceButtons = mapProductsToButtons(matchingTypeProducts);

    if (sourceButtons.length === 0) {
        sourceButtons = mapProductsToButtons(dropProducts);
    }

    return sourceButtons;
}

function synthesizePrebuildBundle (type, targetQty) {
    if (!targetQty) return [];

    const buttonsByHandle = new Map();
    getTypeCatalog(type).forEach((catalogItem) => {
        buttonsByHandle.set(catalogItem.handle, catalogItem.button);
    });

    const prebuildRecipe = getResolvedPrebuildRecipe();
    if (prebuildRecipe.length > 0) {
        const recipeSequence = [];

        prebuildRecipe.forEach((entry) => {
            const sourceButton = buttonsByHandle.get(normalizeHandle(entry.handle));
            if (!sourceButton) return;

            for (let count = 0; count < entry.qty; count += 1) {
                recipeSequence.push(sourceButton);
            }
        });

        if (recipeSequence.length > 0) {
            const recipeBundleMap = new Map();

            for (let i = 0; i < targetQty; i += 1) {
                const sourceButton = recipeSequence[i % recipeSequence.length];
                const sourceHandle = sourceButton.dataset.productHandle;

                if (!recipeBundleMap.has(sourceHandle)) {
                    recipeBundleMap.set(sourceHandle, buildBundleItemFromButton(sourceButton));
                    continue;
                }

                const existingBundleItem = recipeBundleMap.get(sourceHandle);
                existingBundleItem.qty += 1;
            }

            return Array.from(recipeBundleMap.values());
        }
    }

    let sourceButtons = resolveDropSourceButtons(type);

    if (sourceButtons.length === 0) {
        sourceButtons = getDefaultPrebuildButtons(type);
    }

    if (sourceButtons.length === 0) return [];

    const bundleMap = new Map();

    for (let i = 0; i < targetQty; i += 1) {
        const sourceButton = sourceButtons[i % sourceButtons.length];
        const sourceHandle = sourceButton.dataset.productHandle;

        if (!bundleMap.has(sourceHandle)) {
            bundleMap.set(sourceHandle, buildBundleItemFromButton(sourceButton));
            continue;
        }

        const existingBundleItem = bundleMap.get(sourceHandle);
        existingBundleItem.qty += 1;
    }

    return Array.from(bundleMap.values());
}

function maybeApplyPrebuildBundle () {
    if (!prebuildRequested || prebuildApplied) return;

    const type = determinePrebuildType();
    applyTypeSelection(type);

    const size = resolveSizeForType(type);
    applySizeSelection(type, size);

    const selectedSize = selectedBundleSize();
    if (!selectedSize) return;

    const prebuiltBundleItems = synthesizePrebuildBundle(type, selectedSize);
    if (prebuiltBundleItems.length === 0) return;

    bundleItems = prebuiltBundleItems;
    prebuildApplied = true;
    adjustBundle();
}

document.querySelectorAll('input[name="delivery-every"]').forEach((input, index) => {
    const checkedSize = document.querySelector('[name="build-a-box-size"]:checked');
    if (!checkedSize) return;

    const sellingPlans = checkedSize.dataset.sellingPlans.split(';');
    input.value = sellingPlans[index];
});

document.querySelectorAll('[data-section-step="1"] input[name="build-a-box-type"]').forEach(input => {
    input.addEventListener('change', () => {
        document.querySelectorAll('[data-section-step="2"] input[name="build-a-box-size"]').forEach(sizeInput => {
            sizeInput.checked = false;
        });

        document.querySelectorAll('[data-section-step="2"] ul[data-type]').forEach(list => {
            list.setAttribute('hidden', 'hidden');

            if (list.dataset.type === input.value) {
                list.removeAttribute('hidden');
                const firstSizeInput = list.querySelector('li:first-child input');
                if (firstSizeInput) {
                    firstSizeInput.checked = true;
                    firstSizeInput.dispatchEvent(new Event('change'));
                }
            }
        });

        document.querySelectorAll('[data-section-step="3"] .product-list').forEach(list => {
            list.setAttribute('hidden', 'hidden');

            if (list.dataset.type === input.value) {
                list.removeAttribute('hidden');
            }
        });
    });
});

document.querySelectorAll('[data-section-step="2"] input[name="build-a-box-size"]').forEach(input => {
    input.addEventListener('change', () => {
        const subscriptionPriceElement = document.querySelector('[data-section-step="4"] [data-subsription-price]');
        const oneTimePriceElement = document.querySelector('[data-section-step="4"] [data-one-time-price]');

        if (subscriptionPriceElement) {
            subscriptionPriceElement.textContent = Shopify.formatMoney(input.dataset.sellingPlanPrice);
        }

        if (oneTimePriceElement) {
            oneTimePriceElement.textContent = Shopify.formatMoney(input.dataset.price);
        }

        document.querySelectorAll('input[name="delivery-every"]').forEach((deliveryInput, index) => {
            const sellingPlans = input.dataset.sellingPlans.split(';');
            deliveryInput.value = sellingPlans[index];
        });
    });
});

document.querySelectorAll('#build-a-box-breadcrub button').forEach(btn => {
    btn.addEventListener('click', () => {
        document.querySelectorAll('[data-section-step]').forEach(section => {
            section.setAttribute('hidden', 'hidden');

            if (btn.dataset.breadcrumbStep === section.dataset.sectionStep) {
                section.removeAttribute('hidden');
            }
        });

        document.querySelectorAll('#build-a-box-breadcrub button').forEach((button, index) => {
            button.classList.remove('active');

            if (button.dataset.breadcrumbStep === btn.dataset.breadcrumbStep) {
                button.classList.add('active');
            }

            if (Number(button.dataset.breadcrumbStep <= index + 1)) {
                button.disabled = false;
            } else {
                button.disabled = true;
            }
        });
    });
});

document.querySelectorAll('button[data-next-step]').forEach(btn => {
    btn.addEventListener('click', () => {
        btn.closest('[data-section-step]').setAttribute('hidden', 'hidden');
        document.querySelector(`[data-section-step="${btn.dataset.nextStep}"`).removeAttribute('hidden');

        document.querySelectorAll('[data-breadcrumb-step]').forEach((breadcrumbElem) => {
            breadcrumbElem.classList.remove('active');

            if (breadcrumbElem.dataset.breadcrumbStep === btn.dataset.nextStep) {
                breadcrumbElem.classList.add('active');
            }
        });

        if (btn.dataset.nextStep === '2') {
            let description = '';

            switch (selectedBundleType()) {
            case 'kids':
                description = '<p>Choose your preferred box size for <b>Kids Snack Bars</b></p>';
                break;
            case 'protein':
                description = '<p>Choose your preferred size from <b>Protein Bars</b></p>';
                break;
            case 'cookies':
                description = '<p>Choose your preferred box size for <b>Soft-Baked Cookies</b></p>';
                break;
            case 'mixedbundle':
                window.stop()
                window.location.href = 'https://www.skoutorganic.com/a/account/custom-tools/dynamic-box?product=15022956478828'
                return
            }

            document.querySelector('[data-section-step="2"] .bundle-header .description').innerHTML = description;
        }

        if (btn.dataset.nextStep === '3') {
            bundleItems = [];
            adjustBundle();

            const title = `Choose ${selectedBundleSize()} ${selectedBundleType() === 'cookies' ? 'Cookies' : 'Bars'}`;
            document.querySelector('[data-section-step="3"] .bundle-header .title').textContent = title;

            document.querySelectorAll('button[data-next-step="4"]').forEach(nextButton => {
                nextButton.textContent = `${selectedBundleSize()} items left`;
            });

            maybeApplyPrebuildBundle();
        }

        if (btn.dataset.nextStep === '5') {
            buildReviewStep();
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });
    });
});

function buildReviewStep () {
    let productList = '';

    bundleItems.forEach(item => {
        productList += `
            <li>
                <img 
                    class="img-fluid mb-0"
                    src="${item.img}" 
                    alt="${item.title}"
                    width="600"
                    height="600"
                    loading="lazy"> 
                <h3 class="h6 mb-2 text-inherit flex-grow-1">
                    ${item.qty} x ${item.title}
                </h3>
                <p class="opacity-50 small mt-n1 mb-4">
                    Frequency: ${document.querySelector('[name="frequency"]:checked').value}
                </p>
            </li>
        `;
    });

    document.querySelector('#build-a-box-review-step').innerHTML = `
        <ul class="product-list list-unstyled row row-cols-2 row-cols-sm-3 mx-n3 mx-sm-n4 mb-7 text-center">
            ${productList}
        </ul>
    `;
}

document.querySelectorAll('button[data-prev-step]').forEach(btn => {
    btn.addEventListener('click', () => {
        btn.closest('[data-section-step]').setAttribute('hidden', 'hidden');
        document.querySelector(`[data-section-step="${btn.dataset.prevStep}"`).removeAttribute('hidden');

        document.querySelectorAll('[data-breadcrumb-step]').forEach(breadcrumbElem => {
            breadcrumbElem.classList.remove('active');
            if (breadcrumbElem.dataset.breadcrumbStep === btn.dataset.prevStep) {
                breadcrumbElem.classList.add('active');
            }
        });

        window.scrollTo({ top: 0, behavior: 'smooth' });
    });
});

document.querySelectorAll('select#bundle-filter-select').forEach(select => {
    select.addEventListener('change', () => {
        document.querySelectorAll('[data-section-step="3"] .product-item').forEach(item => {
            if (!select.value.length) {
                item.removeAttribute('hidden');
            } else if (item.dataset.curated.includes(select.value)) {
                item.removeAttribute('hidden');
            } else {
                item.setAttribute('hidden', 'hidden');
            }
        });
    });
});

document.querySelectorAll('button[data-add-to-bundle]').forEach(btn => {
    btn.addEventListener('click', () => {
        addToBundle(btn);
    });
});

document.querySelectorAll('button[data-mode]').forEach(btn => {
    btn.addEventListener('click', () => {
        if (btn.dataset.mode === 'plus') {
            addToBundle(btn);
        } else {
            removeFromBundle(btn);
        }
    });
});

document.querySelectorAll('[data-section-step="4"] input[name="frequency"]').forEach(input => {
    input.addEventListener('change', () => {
        if (input.value === 'subscription') {
            document.querySelector('[data-section-step="4"] [data-delivery-every]').removeAttribute('hidden');
        } else {
            document.querySelector('[data-section-step="4"] [data-delivery-every]').setAttribute('hidden', 'hidden');
        }
    });
});

function addToBundle (btn) {
    const productTitle = btn.dataset.productTitle;
    const productHandle = btn.dataset.productHandle;
    const productImg = btn.dataset.productImg;
    const variantId = btn.dataset.variantId;
    const sellingPlans = btn.dataset.sellingPlans.split(';');

    const isInBundle = bundleItems.some((elem) => elem.title === productTitle);

    if (isInBundle) {
        bundleItems = bundleItems.map(item => {
            if (item.title === productTitle) {
                return {
                    ...item,
                    qty: item.qty + 1
                };
            }
            return item;
        });
    } else {
        bundleItems.push({
            title: productTitle,
            handle: productHandle,
            img: productImg,
            variant_id: variantId,
            selling_plans: sellingPlans,
            qty: 1
        });
    }

    adjustBundle();
}

function removeFromBundle (btn) {
    const productTitle = btn.dataset.productTitle;

    const existingItem = bundleItems.find((elem) => elem.title === productTitle);
    if (!existingItem) return;

    if (existingItem.qty === 1) {
        bundleItems = bundleItems.filter(elem => elem.title !== productTitle);
    } else {
        bundleItems = bundleItems.map(elem => {
            if (elem.title === productTitle) {
                return {
                    ...elem,
                    qty: elem.qty - 1
                };
            }
            return elem;
        });
    }

    adjustBundle();
}

function adjustBundle () {
    const bundleSize = selectedBundleSize();
    if (!bundleSize) return;

    const progressWidth = Math.round(getBundleItemsQty() / bundleSize * 100);

    document.querySelectorAll('.progress-bar').forEach(progressBar => {
        progressBar.style.width = `${progressWidth}%`;
    });

    document.querySelectorAll('button[data-next-step="4"]').forEach(btn => {
        if (getBundleItemsQty() === bundleSize) {
            btn.textContent = btn.dataset.text;
            btn.disabled = false;
        } else {
            btn.textContent = `${bundleSize - getBundleItemsQty()} items left`;
            btn.disabled = true;
        }
    });

    document.querySelectorAll('[data-section-step="3"] button[data-add-to-bundle]').forEach(btn => {
        if (bundleItems.some(item => item.title === btn.dataset.productTitle)) {
            btn.setAttribute('hidden', 'hidden');
        } else {
            btn.removeAttribute('hidden');
        }

        if (getBundleItemsQty() === bundleSize) {
            btn.disabled = true;
        } else {
            btn.disabled = false;
        }

        if (btn.dataset.soldout === 'true') {
            btn.disabled = true;
        }
    });

    document.querySelectorAll('.product-item .quantity-wrapper').forEach(wrapper => {
        if (bundleItems.some(item => item.title === wrapper.dataset.productTitle)) {
            wrapper.removeAttribute('hidden');
        } else {
            wrapper.setAttribute('hidden', 'hidden');
        }

        const qtyValue = bundleItems.find(item => item.title === wrapper.dataset.productTitle)?.qty || '0';
        wrapper.querySelectorAll('input').forEach(input => {
            input.value = qtyValue;
        });

        if (getBundleItemsQty() === bundleSize) {
            wrapper.querySelector('[data-mode="plus"]').disabled = true;
        } else {
            wrapper.querySelector('[data-mode="plus"]').disabled = false;
        }
    });

    if (bundleItems.length) {
        document.querySelector('#bundle-contents [data-empty]').setAttribute('hidden', 'hidden');
    } else {
        document.querySelector('#bundle-contents [data-empty]').removeAttribute('hidden');
    }

    document.querySelectorAll('#bundle-contents .product-item').forEach(productItem => {
        if (bundleItems.some(item => item.title === productItem.dataset.productTitle)) {
            productItem.removeAttribute('hidden');
        } else {
            productItem.setAttribute('hidden', 'hidden');
        }
    });
}

document.querySelectorAll('.build-a-box-wrapper button[data-add-to-cart]').forEach(btn => {
    btn.addEventListener('click', async () => {
        btn.innerHTML = `
            <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        `;

        const selectedSizeInput = document.querySelector('[data-section-step="2"] input[name="build-a-box-size"]:checked');
        if (!selectedSizeInput) return;

        const variantId = Number(selectedSizeInput.dataset.variantId);

        const uniqueId = +Math.random().toString(8).slice(2);
        const frequency = document.querySelector('[name="frequency"]:checked').value;

        const sellingPlanId = frequency === 'subscription' ? document.querySelector('[name="delivery-every"]:checked').value : null;

        const { boxId } = await getStaticBox(variantId, sellingPlanId);

        const choices = bundleItems.map(product => `${product.qty} ${product.title}`).join(', ');

        const babProperties = Array.from(bundleItems).reduce((properties, product) => {
            properties[`_pvgid://shopify/ProductVariant/${product.variant_id}`] = product.qty;
            return properties;
        }, {});

        const bundleProducts = Array.from(bundleItems).map(product => {
            let productSellingPlanId = null;

            if (document.querySelector('[name="frequency"]:checked').value === 'subscription') {
                const index = Number(document.querySelector('[name="delivery-every"]:checked').dataset.index);
                productSellingPlanId = Number(product.selling_plans[index]);
            }

            return {
                id: product.variant_id,
                quantity: product.qty,
                properties: {
                    _staticBoxId: boxId,
                    _staticBoxIndex: uniqueId,
                    _bundleQuantity: product.qty
                },
                selling_plan: productSellingPlanId
            };
        });

        const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                items: [
                    {
                        id: variantId,
                        quantity: 1,
                        properties: {
                            Choices: choices,
                            ...babProperties,
                            _staticBoxId: boxId,
                            _staticBoxIndex: uniqueId,
                            _staticBoxParent: 1,
                            _staticBoxSkip: 1
                        },
                        selling_plan: sellingPlanId
                    },
                    ...bundleProducts
                ]
            })
        });

        window.refreshCartContents(response);

        if (document.querySelector('#offcanvas-cart')) {
            bootstrap.Offcanvas.getOrCreateInstance('#offcanvas-cart').show();
        }

        btn.innerHTML = btn.dataset.text;

        document.querySelector('.build-a-box-wrapper').remove();
        document.querySelector('[data-thank-you-step]').removeAttribute('hidden');
    });
});

const initPrebuildFlow = () => {
    const typeForFlow = determinePrebuildType();

    if (typeForFlow) {
        applyTypeSelection(typeForFlow);
    }

    const nextToSizeButton = document.querySelector('.build-a-box-wrapper button[data-next-step="2"]');
    if (nextToSizeButton && (prebuildRequested || requestedType)) {
        nextToSizeButton.dispatchEvent(new Event('click'));
    }

    const resolvedSize = resolveSizeForType(selectedBundleType());
    if (resolvedSize) {
        applySizeSelection(selectedBundleType(), resolvedSize);
    }

    if (prebuildRequested) {
        const nextToFlavorsButton = document.querySelector('.build-a-box-wrapper button[data-next-step="3"]');
        if (nextToFlavorsButton) {
            nextToFlavorsButton.dispatchEvent(new Event('click'));
        }
    }
};

window.setTimeout(initPrebuildFlow, 0);

setInterval(() => {
    const activeBreadcrumbButton = document.querySelector('#build-a-box-breadcrub button.active');
    if (!activeBreadcrumbButton) return;

    const activeStep = Number(activeBreadcrumbButton.dataset.breadcrumbStep);

    document.querySelectorAll('#build-a-box-breadcrub button').forEach((btn, index) => {
        if (index >= activeStep) {
            btn.disabled = true;
        } else {
            btn.disabled = false;
        }
    });
}, 500);

async function getStaticBox (boxVariantId, sellingPlanId) {
    const url = 'https://api.skio.com/storefront-http/get-classic-box-v2';

    try {
        const rawData = await fetch(url, {
            method: 'POST',
            body: JSON.stringify({
                productVariantPlatformId: boxVariantId.toString(),
                sellingPlanPlatformId: sellingPlanId
            })
        });

        const data = await rawData.json();
        return data.ClassicBoxV2;
    } catch (error) {
        throw new Error('Skio: Failed to get box');
    }
}
